import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  Timestamp
} from 'firebase/firestore';
import { startOfWeek, endOfWeek, format } from 'date-fns';
import { it } from 'date-fns/locale';
import { db } from './firebase.config';
import { CompanyContact, ModeratorReport, JobApplication } from '../types';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Ottieni tutte le candidature di tutti gli utenti (controlla anche subcollections)
export const getAllApplications = async (): Promise<JobApplication[]> => {
  try {
    // Prima provo la collection principale
    const applicationsRef = collection(db, 'applications');
    const q = query(applicationsRef, orderBy('appliedDate', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const applications: JobApplication[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      applications.push({ id: doc.id, ...data } as JobApplication);
    });
    
    // Se non trovo nulla nella collection principale, provo a cercare nelle subcollections degli utenti
    if (applications.length === 0) {
      try {
        const usersRef = collection(db, 'users');
        const usersSnapshot = await getDocs(usersRef);
        
        for (const userDoc of usersSnapshot.docs) {
          const userData = userDoc.data();
          const userApplicationsRef = collection(db, 'users', userDoc.id, 'applications');
          try {
            const userApplicationsSnapshot = await getDocs(userApplicationsRef);
            
            userApplicationsSnapshot.forEach((appDoc) => {
              const data = appDoc.data();
              const application = { 
                id: appDoc.id, 
                userId: userDoc.id,
                userName: userData.name || 'N/A',
                userEmail: userData.email || 'N/A',
                ...data 
              } as JobApplication;
              applications.push(application);
            });
          } catch (subError) {
            // Silently handle subcollection errors
          }
        }
      } catch (usersError) {
        // Silently handle users query errors
      }
    }
    
    return applications;
  } catch (error) {
    console.error('❌ Error fetching all applications:', error);
    // Se non ci sono candidature, restituisci array vuoto invece di lanciare errore
    if (error instanceof Error && error.message.includes('No documents')) {
      console.log('ℹ️ Nessuna candidatura trovata, restituisco array vuoto');
      return [];
    }
    throw error;
  }
};

// Ottieni candidature di un utente specifico
export const getUserApplications = async (userId: string): Promise<JobApplication[]> => {
  try {
    const applicationsRef = collection(db, 'applications');
    const q = query(
      applicationsRef, 
      where('userId', '==', userId),
      orderBy('appliedDate', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    const applications: JobApplication[] = [];
    querySnapshot.forEach((doc) => {
      applications.push({ id: doc.id, ...doc.data() } as JobApplication);
    });
    
    return applications;
  } catch (error) {
    console.error('Error fetching user applications:', error);
    throw error;
  }
};

// Aggiungi contatto con azienda (nel database separato del moderatore)
export const addCompanyContact = async (contact: Omit<CompanyContact, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  try {
    console.log('🔍 addCompanyContact: Aggiungendo contatto per moderatore:', contact.moderatorId);
    const contactsRef = collection(db, 'moderatorContacts'); // Collection separata per i contatti del moderatore
    const newContact = {
      ...contact,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };
    
    console.log('🔍 addCompanyContact: Dati contatto:', newContact);
    const docRef = await addDoc(contactsRef, newContact);
    console.log('🔍 addCompanyContact: Contatto aggiunto con ID:', docRef.id);
    
    return docRef.id;
  } catch (error) {
    console.error('❌ Error adding company contact:', error);
    throw error;
  }
};

// Aggiorna contatto con azienda (nel database separato del moderatore)
export const updateCompanyContact = async (contactId: string, updates: Partial<CompanyContact>): Promise<void> => {
  try {
    console.log('🔍 updateCompanyContact: Aggiornando contatto:', contactId);
    const contactRef = doc(db, 'moderatorContacts', contactId); // Collection separata per i contatti del moderatore
    await updateDoc(contactRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });
    console.log('🔍 updateCompanyContact: Contatto aggiornato con successo');
  } catch (error) {
    console.error('❌ Error updating company contact:', error);
    throw error;
  }
};

// Ottieni contatti di un moderatore (database separato del moderatore)
export const getModeratorContacts = async (moderatorId: string): Promise<CompanyContact[]> => {
  try {
    console.log('🔍 getModeratorContacts: Iniziando query per moderatore:', moderatorId);
    const contactsRef = collection(db, 'moderatorContacts'); // Collection separata per i contatti del moderatore
    console.log('🔍 getModeratorContacts: Collection reference creata');
    
    // Query per ottenere tutti i contatti del moderatore
    const q = query(
      contactsRef,
      where('moderatorId', '==', moderatorId)
      // orderBy('contactDate', 'desc') // Commentato temporaneamente per evitare indice
    );
    console.log('🔍 getModeratorContacts: Query creata');
    
    const querySnapshot = await getDocs(q);
    console.log('🔍 getModeratorContacts: QuerySnapshot ottenuta, size:', querySnapshot.size);
    
    const contacts: CompanyContact[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      console.log('🔍 getModeratorContacts: Contatto trovato:', doc.id, data);
      contacts.push({ id: doc.id, ...data } as CompanyContact);
    });
    
    // Ordino manualmente i risultati per data (più recenti prima)
    contacts.sort((a, b) => {
      if (a.contactDate && b.contactDate) {
        return b.contactDate.toMillis() - a.contactDate.toMillis();
      }
      return 0;
    });
    
    console.log('🔍 getModeratorContacts: Totale contatti:', contacts.length);
    return contacts;
  } catch (error) {
    console.error('❌ Error fetching moderator contacts:', error);
    // Se non ci sono contatti, restituisci array vuoto invece di lanciare errore
    if (error instanceof Error && error.message.includes('No documents')) {
      console.log('ℹ️ Nessun contatto trovato, restituisco array vuoto');
      return [];
    }
    throw error;
  }
};

// Ottieni contatti per periodo personalizzato
export const getContactsByPeriod = async (
  moderatorId: string, 
  startDate: Date, 
  endDate: Date
): Promise<CompanyContact[]> => {
  try {
    console.log('🔍 getContactsByPeriod: Filtro per periodo', startDate, 'a', endDate);
    
    // Ottieni tutti i contatti del moderatore e filtra lato client
    const allContacts = await getModeratorContacts(moderatorId);
    
    // Filtra per periodo
    const filteredContacts = allContacts.filter(contact => {
      if (!contact.contactDate) return false;
      const contactDate = contact.contactDate.toDate();
      return contactDate >= startDate && contactDate <= endDate;
    });
    
    console.log('🔍 getContactsByPeriod: Contatti filtrati:', filteredContacts.length, 'su', allContacts.length);
    return filteredContacts;
  } catch (error) {
    console.error('Error fetching contacts by period:', error);
    throw error;
  }
};

// Ottieni contatti settimanali di un moderatore
export const getWeeklyContacts = async (moderatorId: string): Promise<CompanyContact[]> => {
  try {
    const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 }); // Lunedì
    const weekEnd = endOfWeek(new Date(), { weekStartsOn: 1 }); // Domenica
    
    // Usa la stessa funzione per evitare duplicazione
    return await getContactsByPeriod(moderatorId, weekStart, weekEnd);
  } catch (error) {
    console.error('Error fetching weekly contacts:', error);
    throw error;
  }
};

// Genera e salva report delle attività del moderatore
export const generateAndSaveModeratorReport = async (moderatorId: string): Promise<ModeratorReport> => {
  try {
    console.log('📊 Generazione report moderatore:', moderatorId);
    
    // Ottieni tutti i contatti del moderatore (non solo settimanali)
    const allContacts = await getModeratorContacts(moderatorId);
    
    // Filtra contatti degli ultimi 7 giorni
    const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
    const weekEnd = endOfWeek(new Date(), { weekStartsOn: 1 });
    
    const recentContacts = allContacts.filter(contact => {
      if (!contact.contactDate) return false;
      const contactDate = contact.contactDate.toDate();
      return contactDate >= weekStart && contactDate <= weekEnd;
    });
    
    console.log('📊 Contatti trovati:', recentContacts.length);
    
    const summary = {
      totalContacts: recentContacts.length,
      positiveResponses: recentContacts.filter(c => c.response === 'positive').length,
      negativeResponses: recentContacts.filter(c => c.response === 'negative').length,
      pendingResponses: recentContacts.filter(c => c.response === 'pending').length,
      noResponses: recentContacts.filter(c => c.response === 'no-response').length
    };
    
    const report: ModeratorReport = {
      id: `report-${moderatorId}-${Date.now()}`,
      moderatorId,
      weekStart: Timestamp.fromDate(weekStart),
      weekEnd: Timestamp.fromDate(weekEnd),
      contacts: recentContacts,
      summary,
      createdAt: Timestamp.now()
    };
    
    // Salva il report nel database
    const reportsRef = collection(db, 'moderatorReports');
    await addDoc(reportsRef, report);
    
    console.log('📊 Report salvato con successo');
    return report;
  } catch (error) {
    console.error('❌ Error generating and saving moderator report:', error);
    throw error;
  }
};

// Genera report per periodo personalizzato
export const generateCustomPeriodReport = async (
  moderatorId: string, 
  startDate: Date, 
  endDate: Date
): Promise<ModeratorReport> => {
  try {
    console.log('📊 Generazione report personalizzato:', moderatorId, startDate, endDate);
    
    const contacts = await getContactsByPeriod(moderatorId, startDate, endDate);
    
    console.log('📊 Contatti trovati:', contacts.length);
    
    const summary = {
      totalContacts: contacts.length,
      positiveResponses: contacts.filter(c => c.response === 'positive').length,
      negativeResponses: contacts.filter(c => c.response === 'negative').length,
      pendingResponses: contacts.filter(c => c.response === 'pending').length,
      noResponses: contacts.filter(c => c.response === 'no-response').length
    };
    
    const report: ModeratorReport = {
      id: `report-${moderatorId}-${Date.now()}`,
      moderatorId,
      weekStart: Timestamp.fromDate(startDate),
      weekEnd: Timestamp.fromDate(endDate),
      contacts,
      summary,
      createdAt: Timestamp.now()
    };
    
    // Salva il report nel database
    const reportsRef = collection(db, 'moderatorReports');
    await addDoc(reportsRef, report);
    
    console.log('📊 Report personalizzato salvato con successo');
    return report;
  } catch (error) {
    console.error('❌ Error generating custom period report:', error);
    throw error;
  }
};

// Genera report settimanale per un moderatore (deprecato)
export const generateWeeklyReport = async (moderatorId: string): Promise<ModeratorReport> => {
  try {
    const contacts = await getWeeklyContacts(moderatorId);
    const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
    const weekEnd = endOfWeek(new Date(), { weekStartsOn: 1 });
    
    const summary = {
      totalContacts: contacts.length,
      positiveResponses: contacts.filter(c => c.response === 'positive').length,
      negativeResponses: contacts.filter(c => c.response === 'negative').length,
      pendingResponses: contacts.filter(c => c.response === 'pending').length,
      noResponses: contacts.filter(c => c.response === 'no-response').length
    };
    
    const report: ModeratorReport = {
      id: `report-${moderatorId}-${Date.now()}`,
      moderatorId,
      weekStart: Timestamp.fromDate(weekStart),
      weekEnd: Timestamp.fromDate(weekEnd),
      contacts,
      summary,
      createdAt: Timestamp.now()
    };
    
    return report;
  } catch (error) {
    console.error('Error generating weekly report:', error);
    throw error;
  }
};

// Ottieni tutti i report di un moderatore
export const getModeratorReports = async (moderatorId: string): Promise<ModeratorReport[]> => {
  try {
    const reportsRef = collection(db, 'moderatorReports');
    const q = query(
      reportsRef,
      where('moderatorId', '==', moderatorId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    const reports: ModeratorReport[] = [];
    querySnapshot.forEach((doc) => {
      reports.push({ id: doc.id, ...doc.data() } as ModeratorReport);
    });
    
    return reports;
  } catch (error) {
    console.error('Error fetching moderator reports:', error);
    throw error;
  }
};

// Genera PDF del report del moderatore
export const generateModeratorReportPDF = async (report: ModeratorReport, moderatorName: string): Promise<void> => {
  try {
    console.log('📄 Generazione PDF report...');
    
    // Crea un elemento HTML temporaneo per il report
    const reportElement = document.createElement('div');
    reportElement.style.padding = '20px';
    reportElement.style.fontFamily = 'Arial, sans-serif';
    reportElement.style.fontSize = '12px';
    reportElement.style.lineHeight = '1.4';
    reportElement.style.color = '#333';
    reportElement.style.backgroundColor = 'white';
    reportElement.style.width = '800px';
    
    // Header del report
    const header = `
      <div style="text-align: center; margin-bottom: 30px; border-bottom: 2px solid #3b82f6; padding-bottom: 20px;">
        <h1 style="color: #3b82f6; margin: 0; font-size: 24px;">Report Attività Moderatore</h1>
        <p style="margin: 5px 0; color: #666;">${moderatorName}</p>
        <p style="margin: 5px 0; color: #666;">
          Periodo: ${format(report.weekStart.toDate(), 'dd/MM/yyyy', { locale: it })} - ${format(report.weekEnd.toDate(), 'dd/MM/yyyy', { locale: it })}
        </p>
        <p style="margin: 5px 0; color: #666;">
          Generato il: ${format(report.createdAt.toDate(), 'dd/MM/yyyy HH:mm', { locale: it })}
        </p>
      </div>
    `;
    
    // Riepilogo statistiche
    const summary = `
      <div style="margin-bottom: 30px;">
        <h2 style="color: #3b82f6; border-bottom: 1px solid #e5e7eb; padding-bottom: 10px;">Riepilogo Attività</h2>
        <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-top: 15px;">
          <div style="text-align: center; padding: 15px; background: #f3f4f6; border-radius: 8px;">
            <div style="font-size: 24px; font-weight: bold; color: #3b82f6;">${report.summary.totalContacts}</div>
            <div style="font-size: 12px; color: #666;">Contatti Totali</div>
          </div>
          <div style="text-align: center; padding: 15px; background: #dcfce7; border-radius: 8px;">
            <div style="font-size: 24px; font-weight: bold; color: #16a34a;">${report.summary.positiveResponses}</div>
            <div style="font-size: 12px; color: #666;">Risposte Positive</div>
          </div>
          <div style="text-align: center; padding: 15px; background: #fef3c7; border-radius: 8px;">
            <div style="font-size: 24px; font-weight: bold; color: #d97706;">${report.summary.pendingResponses}</div>
            <div style="font-size: 12px; color: #666;">In Attesa</div>
          </div>
          <div style="text-align: center; padding: 15px; background: #fee2e2; border-radius: 8px;">
            <div style="font-size: 24px; font-weight: bold; color: #dc2626;">${report.summary.negativeResponses}</div>
            <div style="font-size: 12px; color: #666;">Risposte Negative</div>
          </div>
        </div>
      </div>
    `;
    
    // Dettagli contatti
    let contactsDetails = `
      <div style="margin-bottom: 30px;">
        <h2 style="color: #3b82f6; border-bottom: 1px solid #e5e7eb; padding-bottom: 10px;">Dettagli Contatti</h2>
    `;
    
    if (report.contacts.length === 0) {
      contactsDetails += `
        <p style="text-align: center; color: #666; font-style: italic; padding: 20px;">
          Nessun contatto effettuato in questo periodo
        </p>
      `;
    } else {
      report.contacts.forEach((contact, index) => {
        const contactDate = contact.contactDate ? format(contact.contactDate.toDate(), 'dd/MM/yyyy HH:mm', { locale: it }) : 'N/A';
        const responseColor = contact.response === 'positive' ? '#16a34a' : 
                             contact.response === 'negative' ? '#dc2626' : 
                             contact.response === 'pending' ? '#d97706' : '#666';
        const responseText = contact.response === 'positive' ? 'Positiva' : 
                           contact.response === 'negative' ? 'Negativa' : 
                           contact.response === 'pending' ? 'In Attesa' : 'Nessuna';
        
        contactsDetails += `
          <div style="border: 1px solid #e5e7eb; border-radius: 8px; padding: 15px; margin-bottom: 15px; background: #fafafa;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
              <h3 style="margin: 0; color: #3b82f6; font-size: 16px;">${contact.companyName}</h3>
              <span style="color: ${responseColor}; font-weight: bold;">${responseText}</span>
            </div>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; font-size: 11px;">
              <div><strong>Data Contatto:</strong> ${contactDate}</div>
              <div><strong>Metodo:</strong> ${contact.contactMethod}</div>
              <div><strong>Persona Contattata:</strong> ${contact.contactPerson || 'N/A'}</div>
              <div><strong>ID Candidatura:</strong> ${contact.applicationId}</div>
            </div>
            ${contact.notes ? `<div style="margin-top: 10px;"><strong>Note:</strong> ${contact.notes}</div>` : ''}
          </div>
        `;
      });
    }
    
    contactsDetails += '</div>';
    
    // Assembla il contenuto completo
    reportElement.innerHTML = header + summary + contactsDetails;
    
    // Aggiungi al DOM temporaneamente
    document.body.appendChild(reportElement);
    
    // Genera il PDF
    const canvas = await html2canvas(reportElement, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff'
    });
    
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgWidth = 210;
    const pageHeight = 295;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;
    
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
    
    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }
    
    // Rimuovi l'elemento temporaneo
    document.body.removeChild(reportElement);
    
    // Scarica il PDF
    const fileName = `report-moderatore-${moderatorName.replace(/\s+/g, '-')}-${format(new Date(), 'yyyy-MM-dd')}.pdf`;
    pdf.save(fileName);
    
    console.log('📄 PDF generato con successo:', fileName);
  } catch (error) {
    console.error('❌ Error generating PDF:', error);
    throw error;
  }
};

// Esporta report in formato CSV
export const exportReportToCSV = (contacts: CompanyContact[], period: { start: Date; end: Date }): void => {
  try {
    const headers = [
      'Azienda',
      'Data Contatto',
      'Metodo',
      'Persona Contattata',
      'Risposta',
      'Note',
      'Data Follow-up',
      'ID Candidatura'
    ];
    
    const rows = contacts.map(contact => [
      contact.companyName,
      contact.contactDate ? format(contact.contactDate.toDate(), 'dd/MM/yyyy HH:mm', { locale: it }) : 'N/A',
      contact.contactMethod,
      contact.contactPerson || 'N/A',
      contact.response === 'positive' ? 'Positiva' : 
      contact.response === 'negative' ? 'Negativa' : 
      contact.response === 'pending' ? 'In Attesa' : 'Nessuna',
      contact.notes || '',
      contact.followUpDate ? format(contact.followUpDate.toDate(), 'dd/MM/yyyy', { locale: it }) : 'N/A',
      String(contact.applicationId || '')
    ]);
    
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `report-contatti-${format(period.start, 'yyyy-MM-dd')}-${format(period.end, 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    console.log('📄 CSV esportato con successo');
  } catch (error) {
    console.error('❌ Error exporting CSV:', error);
    throw error;
  }
};

// Genera statistiche per trend temporale
export const generateTrendData = (reports: ModeratorReport[]) => {
  return reports.map(report => ({
    period: format(report.weekStart.toDate(), 'dd/MM', { locale: it }),
    contatti: report.summary.totalContacts,
    positive: report.summary.positiveResponses,
    negative: report.summary.negativeResponses,
    pending: report.summary.pendingResponses,
    noResponse: report.summary.noResponses
  }));
}; 